import React from 'react'
import { useThemedColours } from 'hooks/useTheme'

// The preview's columns: the one in focus between the edges of its neighbours.
const PREVIEW_COLUMNS = ['64px', '136px', '64px']
const PREVIEW_ROWS = 3

/**
 * A sliver of a table in a header's colours: a header between the edges of its neighbours, over a few
 * rows, all fading out away from the column in the middle. Square-cornered, as the grid is. Above the
 * header colours in the hub's Tables settings, and in a column's Header formatting.
 */
const HeaderPreview: React.FC<{ background: string; text: string; label?: React.ReactNode }> = ({
  background,
  text,
  label = 'Column'
}) => {
  const themed = useThemedColours()
  const header = themed.header(background, text)
  // Fading out to each side, and downwards: the two masks overlap to leave the middle column's header.
  const fade = [
    'linear-gradient(to right, transparent, #000 24%, #000 76%, transparent)',
    'linear-gradient(to bottom, #000 50%, transparent 110%)'
  ].join(', ')
  return (
    <div
      aria-hidden
      data-testid="column-header-preview"
      className="grid border-l border-t border-divider text-[12px]"
      style={{
        gridTemplateColumns: PREVIEW_COLUMNS.join(' '),
        maskImage: fade,
        WebkitMaskImage: fade,
        maskComposite: 'intersect',
        WebkitMaskComposite: 'source-in'
      }}
    >
      {PREVIEW_COLUMNS.map((_, column) => (
        <div
          key={`header-${column}`}
          // Set as the table's own headers are: 13px semibold.
          className="flex h-7 items-center border-b border-r border-divider px-2 text-[13px] font-semibold"
          style={header}
        >
          {column === 1 && <span className="flex min-w-0 items-center gap-1.5 truncate">{label}</span>}
        </div>
      ))}
      {Array.from({ length: PREVIEW_ROWS * PREVIEW_COLUMNS.length }, (_, cell) => (
        <div key={`cell-${cell}`} className="flex h-6 items-center border-b border-r border-divider bg-surface px-2">
          <span className={`h-1.5 rounded-full bg-divider ${cell % 3 === 1 ? 'w-16' : 'w-8'}`} />
        </div>
      ))}
    </div>
  )
}

export default HeaderPreview
